import type { ReactNode } from 'react';

/**
 * The classic toolbar's icons: small coloured 16px drawings in the style of a desktop trading terminal (our own drawings).
 * They are decorative: every button that shows one also has a label.
 */
const Svg = ({ children }: { children: ReactNode }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
    shapeRendering="crispEdges"
  >
    {children}
  </svg>
);

const BLUE = '#1d6fd1';
const GREEN = '#1a9b35';
const RED = '#d4261f';
const DARK = '#303030';
const GOLD = '#e0a000';

const CLASSIC_ICONS = {
  newChart: (
    <Svg>
      <rect x="1.5" y="2.5" width="13" height="11" fill="#fff" stroke={DARK} />
      <path d="M4 11V7M7 11V5M10 11V8M13 11V4" stroke={GREEN} strokeWidth="1.5" />
      <path d="M11 1v4M9 3h4" stroke={GOLD} strokeWidth="1.5" />
    </Svg>
  ),
  profiles: (
    <Svg>
      <path d="M1.5 4.5h5l1 1.5h7v7.5h-13z" fill="#f5d77a" stroke="#a07a10" />
    </Svg>
  ),
  marketWatch: (
    <Svg>
      <rect x="1.5" y="1.5" width="13" height="13" fill="#fff" stroke={DARK} />
      <path d="M3 5h10M3 8h10M3 11h10" stroke={BLUE} />
      <path d="M8 3v11" stroke="#999" />
    </Svg>
  ),
  dataWindow: (
    <Svg>
      <rect x="1.5" y="1.5" width="13" height="13" fill="#fff" stroke={DARK} />
      <path d="M1.5 4.5h13" stroke={DARK} />
      <path d="M3 7h4M3 9.5h4M3 12h4M9 7h4M9 9.5h4M9 12h4" stroke="#777" />
    </Svg>
  ),
  navigator: (
    <Svg>
      <rect x="1.5" y="1.5" width="13" height="13" fill="#fff" stroke={DARK} />
      <path d="M3.5 4h3M5 4v7h3M5 7.5h3" stroke={DARK} fill="none" />
      <rect x="8.5" y="6" width="4" height="3" fill={GOLD} />
      <rect x="8.5" y="9.5" width="4" height="3" fill={BLUE} />
    </Svg>
  ),
  toolbox: (
    <Svg>
      <rect x="1.5" y="1.5" width="13" height="13" fill="#fff" stroke={DARK} />
      <rect x="1.5" y="9.5" width="13" height="5" fill="#cfe0f5" stroke={DARK} />
    </Svg>
  ),
  newOrder: (
    <Svg>
      <rect x="2.5" y="1.5" width="10" height="13" fill="#fff" stroke={DARK} />
      <path d="M4.5 5h6M4.5 7.5h6M4.5 10h3" stroke="#888" />
      <circle cx="12" cy="12" r="3.5" fill={GREEN} />
      <path d="M12 10v4M10 12h4" stroke="#fff" strokeWidth="1.3" />
    </Svg>
  ),
  bars: (
    <Svg>
      <path
        d="M4 3v9M2.5 5H4M4 10h1.5M10 5v9M8.5 8H10M10 12h1.5"
        stroke={DARK}
        strokeWidth="1.3"
        fill="none"
      />
    </Svg>
  ),
  candles: (
    <Svg>
      <path d="M4.5 2v12M11.5 2v12" stroke={DARK} />
      <rect x="2.5" y="4.5" width="4" height="6" fill="#fff" stroke={DARK} />
      <rect x="9.5" y="5.5" width="4" height="5" fill={DARK} stroke={DARK} />
    </Svg>
  ),
  line: (
    <Svg>
      <path d="M1.5 12l4-5 3 3 6-7" stroke={BLUE} strokeWidth="1.5" fill="none" shapeRendering="auto" />
    </Svg>
  ),
  zoomIn: (
    <Svg>
      <circle cx="6.5" cy="6.5" r="4.5" fill="#fff" stroke={DARK} shapeRendering="auto" />
      <path d="M10 10l4.5 4.5" stroke={DARK} strokeWidth="2" />
      <path d="M4.5 6.5h4M6.5 4.5v4" stroke={GREEN} strokeWidth="1.3" />
    </Svg>
  ),
  zoomOut: (
    <Svg>
      <circle cx="6.5" cy="6.5" r="4.5" fill="#fff" stroke={DARK} shapeRendering="auto" />
      <path d="M10 10l4.5 4.5" stroke={DARK} strokeWidth="2" />
      <path d="M4.5 6.5h4" stroke={RED} strokeWidth="1.3" />
    </Svg>
  ),
  autoScroll: (
    <Svg>
      <path d="M2 8h9" stroke={DARK} strokeWidth="1.5" />
      <path d="M9 4.5L13 8l-4 3.5z" fill={GREEN} />
      <path d="M14.5 2v12" stroke={DARK} />
    </Svg>
  ),
  chartShift: (
    <Svg>
      <path d="M1.5 12l3-4 3 2 3-5" stroke={BLUE} fill="none" shapeRendering="auto" />
      <path d="M12.5 2v12" stroke={RED} strokeDasharray="1.5 1.5" />
      <path d="M13 2.5l2 1.5-2 1.5z" fill={RED} />
    </Svg>
  ),
  indicators: (
    <Svg>
      <path d="M3 13c1-6 2-9 4-9M2.5 8h4" stroke={DARK} strokeWidth="1.4" fill="none" shapeRendering="auto" />
      <path d="M11 7v6M8 10h6" stroke={GREEN} strokeWidth="1.6" />
    </Svg>
  ),
  templates: (
    <Svg>
      <rect x="1.5" y="3.5" width="10" height="9" fill="#fff" stroke={DARK} />
      <rect x="4.5" y="1.5" width="10" height="9" fill="#e8f0fb" stroke={BLUE} />
    </Svg>
  ),
  periods: (
    <Svg>
      <circle cx="8" cy="8" r="6" fill="#fff" stroke={DARK} shapeRendering="auto" />
      <path d="M8 4v4l3 2" stroke={BLUE} strokeWidth="1.4" fill="none" />
    </Svg>
  ),
  cursor: (
    <Svg>
      <path d="M3 1.5v11l3-3 2 5 2-1-2-4.5h4z" fill="#fff" stroke={DARK} shapeRendering="auto" />
    </Svg>
  ),
  crosshair: (
    <Svg>
      <path d="M8 1v14M1 8h14" stroke={DARK} />
      <circle cx="8" cy="8" r="3" fill="none" stroke={DARK} shapeRendering="auto" />
    </Svg>
  ),
  vline: (
    <Svg>
      <path d="M8 1v14" stroke={DARK} strokeWidth="1.5" />
    </Svg>
  ),
  hline: (
    <Svg>
      <path d="M1 8h14" stroke={DARK} strokeWidth="1.5" />
    </Svg>
  ),
  trend: (
    <Svg>
      <path d="M2 13L14 3" stroke={DARK} strokeWidth="1.5" shapeRendering="auto" />
      <circle cx="2.5" cy="12.5" r="1.3" fill={BLUE} />
      <circle cx="13.5" cy="3.5" r="1.3" fill={BLUE} />
    </Svg>
  ),
  channel: (
    <Svg>
      <path d="M1 10L11 2M5 14l10-8" stroke={DARK} strokeWidth="1.3" shapeRendering="auto" />
    </Svg>
  ),
  fib: (
    <Svg>
      <path d="M1 3h14M1 6h14M1 9.5h14M1 13h14" stroke={GOLD} />
      <path d="M2 13L14 3" stroke={DARK} strokeDasharray="2 1.5" shapeRendering="auto" />
    </Svg>
  ),
  text: (
    <Svg>
      <path d="M3 3h10M8 3v10M6 13h4" stroke={DARK} strokeWidth="1.6" />
    </Svg>
  ),
  label: (
    <Svg>
      <path d="M1.5 4.5h9l4 3.5-4 3.5h-9z" fill="#fff" stroke={DARK} />
      <path d="M3.5 8h5" stroke={BLUE} />
    </Svg>
  ),
  shapes: (
    <Svg>
      <rect x="1.5" y="6.5" width="7" height="7" fill="#cfe0f5" stroke={BLUE} />
      <path d="M10.5 2l4 6.5h-8z" fill="#fde2b0" stroke={GOLD} shapeRendering="auto" />
    </Svg>
  ),
  arrows: (
    <Svg>
      <path d="M8 2l5 6h-3v6H6V8H3z" fill={GREEN} stroke="#136e27" shapeRendering="auto" />
    </Svg>
  ),
  camera: (
    <Svg>
      <rect x="1.5" y="4.5" width="13" height="9" fill="#ddd" stroke={DARK} />
      <rect x="5" y="2.5" width="5" height="2" fill={DARK} />
      <circle cx="8" cy="9" r="2.5" fill="#fff" stroke={DARK} shapeRendering="auto" />
    </Svg>
  ),
  search: (
    <Svg>
      <circle cx="6.5" cy="6.5" r="4.5" fill="none" stroke="#666" shapeRendering="auto" />
      <path d="M10 10l4.5 4.5" stroke="#666" strokeWidth="1.6" />
    </Svg>
  ),
  oneClick: (
    <Svg>
      <rect x="1.5" y="4.5" width="6" height="7" fill={RED} />
      <rect x="8.5" y="4.5" width="6" height="7" fill={BLUE} />
    </Svg>
  ),
  properties: (
    <Svg>
      <rect x="1.5" y="2.5" width="13" height="11" fill="#fff" stroke={DARK} />
      <path d="M4 6h8M4 9h5" stroke="#777" />
      <path d="M11 8.5l2 2-2 2" stroke={BLUE} fill="none" />
    </Svg>
  ),
} as const;

export type ClassicIconName = keyof typeof CLASSIC_ICONS;

export const ClassicIcon = ({ name }: { name: ClassicIconName }) => CLASSIC_ICONS[name];
